import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  Assignment as SurveyIcon,
  TrendingUp as TrendingIcon,
  Nature as PlantIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SurveyStatistics, SeedSurvey } from '../types';
import apiService from '../services/api';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  
  return (
    <Card 
      sx={{ 
        height: '140px',
        position: 'relative', 
        overflow: 'visible',
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${color}25`,
          border: `1px solid ${color}50`,
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%' }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
          height: '100%'
        }}>
          <Box sx={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
            <Typography 
              color="textSecondary" 
              gutterBottom 
              variant="body2"
              sx={{ 
                fontWeight: 500,
                fontSize: '0.875rem',
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h3" 
              component="div" 
              sx={{ 
                color: color, 
                fontWeight: 700,
                fontSize: '2.5rem',
                lineHeight: 1
              }}
            >
              {value.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: 3,
              bgcolor: `${color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
              boxShadow: `0 4px 12px ${color}25`
            }}
          >
            <Box sx={{ fontSize: '2rem' }}>
              {icon}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [statistics, setStatistics] = useState<SurveyStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const stats = await apiService.getSurveyStatistics();
      setStatistics(stats);
    } catch (err: any) {
      setError(t('loadError'));
      console.error('Error loading statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
      minHeight: '100vh',
      bgcolor: '#f8fafc'
    }}>
      {/* Welcome Header */}
      <Box sx={{ 
        textAlign: i18n.language === 'ar' ? 'right' : 'left',
        p: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: 3,
        mb: 4
      }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          {t('dashboard')}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
          {t('welcomeMessage')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: 3,
        mb: 4
      }}>
        <StatCard
          title={t('totalSurveys')}
          value={statistics?.total_surveys || 0}
          icon={<SurveyIcon />}
          color={theme.palette.primary.main}
        />
        <StatCard
          title={t('surveysByCrop')}
          value={Object.keys(statistics?.surveys_by_crop || {}).length}
          icon={<PlantIcon />}
          color="#10b981"
        />
        <StatCard
          title={t('governorates')}
          value={Object.keys(statistics?.surveys_by_governorate || {}).length}
          icon={<LocationIcon />}
          color="#f59e0b"
        />
        <StatCard
          title={t('recentSurveys')}
          value={statistics?.recent_surveys?.length || 0}
          icon={<TrendingIcon />}
          color="#ef4444"
        />
      </Box>

      {/* Charts and Lists */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 4,
        direction: i18n.language === 'ar' ? 'rtl' : 'ltr'
      }}>
        {/* Recent Surveys */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            background: 'white'
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              textAlign: i18n.language === 'ar' ? 'right' : 'left',
              fontWeight: 600,
              color: '#1e293b',
              mb: 3
            }}
          >
            {t('recentSurveys')}
          </Typography>
          <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {statistics?.recent_surveys && statistics.recent_surveys.length > 0 ? (
              <List sx={{ p: 0 }}>
                {statistics.recent_surveys.map((survey, index) => (
                  <ListItem 
                    key={survey.id} 
                    divider={index < statistics.recent_surveys.length - 1}
                    sx={{ 
                      direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                      py: 2,
                      px: 0,
                      '&:hover': {
                        bgcolor: '#f8fafc',
                        borderRadius: 2
                      }
                    }}
                  >
                    <ListItemText
                      sx={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
                      primary={
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          justifyContent: i18n.language === 'ar' ? 'flex-end' : 'flex-start'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {survey.survey_number}
                          </Typography>
                          <Chip
                            label={i18n.language === 'ar' ? survey.crop_details?.name_ar : survey.crop_details?.name_en}
                            size="small"
                            sx={{
                              bgcolor: '#e0f2fe',
                              color: '#0277bd',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ textAlign: i18n.language === 'ar' ? 'right' : 'left', mt: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {survey.farmer_name} • {i18n.language === 'ar' ? survey.village_details?.name_ar : survey.village_details?.name_en}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(survey.date_collected)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px',
                color: 'text.secondary'
              }}>
                <SurveyIcon sx={{ fontSize: '3rem', mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" sx={{ opacity: 0.7 }}>
                  {t('noData')}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Surveys by Crop */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            background: 'white'
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              textAlign: i18n.language === 'ar' ? 'right' : 'left',
              fontWeight: 600,
              color: '#1e293b',
              mb: 3
            }}
          >
            {t('surveysByCrop')}
          </Typography>
          <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {statistics?.surveys_by_crop && Object.keys(statistics.surveys_by_crop).length > 0 ? (
              <List sx={{ p: 0 }}>
                {Object.entries(statistics.surveys_by_crop).map(([crop, count], index) => (
                  <ListItem 
                    key={crop} 
                    divider={index < Object.keys(statistics.surveys_by_crop).length - 1}
                    sx={{ 
                      direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                      py: 2,
                      px: 0,
                      '&:hover': {
                        bgcolor: '#f8fafc',
                        borderRadius: 2
                      }
                    }}
                  >
                    <ListItemText
                      sx={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {crop}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {count} {t('surveys').toLowerCase()}
                        </Typography>
                      }
                    />
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        bgcolor: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        [i18n.language === 'ar' ? 'mr' : 'ml']: 2
                      }}
                    >
                      {count}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '200px',
                color: 'text.secondary'
              }}>
                <PlantIcon sx={{ fontSize: '3rem', mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" sx={{ opacity: 0.7 }}>
                  {t('noData')}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
